import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
} from '@angular/core';
import { TokenService } from '../services/token.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LogService } from '../services/log.service';
import { SipService } from '../services/sip.service';
import { BehaviorSubject, combineLatest, Subject, takeUntil } from 'rxjs';
import { UserPresenceState } from '../enums/user-presence-state.enum';
import { ContactService } from '../services/contact.service';
import { MatDialog } from '@angular/material/dialog';
import { UserProfileDialogComponent } from '../user-profile-dialog/user-profile-dialog.component';
import {
  ConferenceRoom,
  ConferenceService,
} from '../services/conference.service';
import { DialogInfoService } from '../services/dialog-info.service';
import { EveCall } from '../models/eve-call';
import { Dialog } from '../models/dialog';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  isAuthenticated = false;
  selectedTab: 'dialer' | 'contacts' | 'history' | 'calls' =
    'contacts';

  presenceClassSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
    'presence-none'
  );
  presenceClass$ = this.presenceClassSubject.asObservable();

  avatarUrl: string = 'assets/eveuser-avatar.png';
  userPresentState: UserPresenceState = UserPresenceState.OutOfOffice;
  eveUser!: any;

  private destroySubject = new Subject<void>();

  @ViewChild('remoteAudio') remoteAudio!: ElementRef<HTMLAudioElement>;

  filteredActiveCalls: EveCall[] = [];
  incomingCalls: EveCall[] = [];

  public activeDialogs: Dialog[] | null = null;
  public incomingDialogs: Dialog[] | null = null;
  public conferences: ConferenceRoom[] | null = null;

  private previousHasCallsOrDialogsOrConferences: boolean = false;
  private isMobileView: boolean = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.handleScreenSizeChange();
  }

  constructor(
    private tokenService: TokenService,
    private logService: LogService,
    private sipService: SipService,
    private contactService: ContactService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private conferenceService: ConferenceService,
    private dialogInfoService: DialogInfoService
  ) {
    this.isAuthenticated =
      this.tokenService.isEveportalJwtTokenValid() &&
      this.tokenService.isRefreshTokenValid();
    this.setPresenceClass();
    this.loadAvatar();
    this.setEveUser();
  }
  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
  ngAfterViewInit() {
    console.log(
      '🎧 Remote audio element initialized:',
      this.remoteAudio.nativeElement
    );
    if (this.remoteAudio?.nativeElement) {
      const el = this.remoteAudio.nativeElement;
      el.muted = false;
      el.volume = 1;

      this.sipService.setAudioElement(el); // ✅ Pass to sipService
      this.conferenceService.bindOutput(el); // ✅ Pass to conferenceService
    }
  }

  // ✅ Bind the remote audio stream to the audio element
  attachAudioStream(stream: MediaStream) {
    if (this.remoteAudio && this.remoteAudio.nativeElement) {
      this.remoteAudio.nativeElement.srcObject = stream;
    }
  }

  ngOnInit() {
    // Initialize screen size check
    this.isMobileView = window.innerWidth < 1024;
    
    // ✅ Subscribe to SIP.js updates
    this.sipService.remoteStream$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((stream) => {
        if (stream) {
          this.attachAudioStream(stream);
        }
      });

    // Subscribe to active calls from SIP service
    this.sipService.activeCalls$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((calls) => {
        this.filteredActiveCalls = calls.filter((c) => {
          return c.isInConference === false;
        });
        this.handleCallsOrDialogsChange();
      });

    // Subscribe to incoming calls observable.
    this.sipService.incomingCalls$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((calls) => {
        this.incomingCalls = calls;
        this.handleCallsOrDialogsChange();
      });

    this.sipService.dialogInfoXml$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((xml: any) => {
        const dialogInfo = this.dialogInfoService.loadDialogInfo(xml);
        console.log('Loaded dialog info:', dialogInfo);
        if (dialogInfo.state === 'full') {
          this.setCompleteActiveIncomingDialogs(dialogInfo.dialogs);
        } else {
          this.setPartialActiveIncomingDialogs(dialogInfo.dialogs);
        }
        this.handleCallsOrDialogsChange();
      });

    this.conferenceService.conferences$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((conferences) => {
        // Handle active conference updates
        this.conferences = conferences;
        this.handleCallsOrDialogsChange();
      });
  }

  downloadEncryptedLog() {
    this.logService.downloadAllLogsAsZip();
  }

  setPresenceClass() {
    this.sipService.userPresence$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status: UserPresenceState) => {
          this.userPresentState = status;
          switch (status) {
            case UserPresenceState.Available:
              this.presenceClassSubject.next('presence-green');
              break;
            case UserPresenceState.Busy:
            case UserPresenceState.Lunch:
            case UserPresenceState.Meeting:
              this.presenceClassSubject.next('presence-red');
              break;
            case UserPresenceState.Holiday:
            case UserPresenceState.OutOfOffice:
              this.presenceClassSubject.next('presence-black');
              break;
            default:
              this.presenceClassSubject.next('presence-grey');
          }
        },
      });
  }

  loadAvatar() {
    const eveUserName =
      this.tokenService.getLoginRefreshToken().eveUser.eveUserName;
    console.log(eveUserName);
    this.contactService
      .getAvatar(eveUserName)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          this.avatarUrl = this.sanitizer.bypassSecurityTrustUrl(
            objectUrl
          ) as string;
        },
        error: () => {
          this.avatarUrl = 'assets/eveuser-avatar.png';
        },
      });
  }

  openUserProfile(): void {
    // Create array of presence states with key-value pairs
    const presenceStates = Object.keys(UserPresenceState)
      .filter((key) => isNaN(Number(key))) // Filter out numeric keys
      .map((key) => ({
        key: UserPresenceState[key as keyof typeof UserPresenceState],
        value:
          UserPresenceState[key as keyof typeof UserPresenceState].valueOf(),
      }));

    // Use responsive width - 100vw on mobile, 450px on desktop
    const isSmallScreen = window.innerWidth < 768;
    const dialogWidth = isSmallScreen ? '100vw' : '450px';

    const dialogRef = this.dialog.open(UserProfileDialogComponent, {
      width: dialogWidth,
      maxWidth: dialogWidth,
      data: {
        eveUserId: this.eveUser.eveUserId,
        firstName: this.eveUser.firstName,
        surname: this.eveUser.surname,
        username: this.eveUser.eveUserName,
        phoneNumber: this.eveUser.number?.number,
        internalNumber: this.eveUser.internalNumber?.number,
        avatarUrl: this.avatarUrl,
        presenceClass$: this.presenceClass$,
        currentPresence: this.userPresentState,
        availablePresenceStates: presenceStates,
      },
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop',
      panelClass: 'user-profile-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  private setEveUser() {
    this.tokenService.eveUser$.pipe(takeUntil(this.destroySubject)).subscribe({
      next: (eveUser) => {
        this.eveUser = eveUser;
      },
    });
  }

  get hasCallsOrDialogsOrConferences(): boolean {
    return (
      this.filteredActiveCalls.length > 0 ||
      this.incomingCalls.length > 0 ||
      (Array.isArray(this.activeDialogs) && this.activeDialogs.length > 0) ||
      (Array.isArray(this.incomingDialogs) &&
        this.incomingDialogs.length > 0) ||
      (Array.isArray(this.conferences) && this.conferences.length > 0)
    );
  }

  private setCompleteActiveIncomingDialogs(dialogs: Dialog[]) {
    // clear incomingDialogs and activeDialogs
    this.incomingDialogs = [];
    this.activeDialogs = [];

    // loop through the  dialogs and set their state
    dialogs.forEach((dialog) => {
      if (dialog.state === 'confirmed') {
        this.activeDialogs!.push(dialog);
      } else {
        this.incomingDialogs!.push(dialog);
      }
    });

    this.activeDialogs = [...this.activeDialogs!];
    this.incomingDialogs = [...this.incomingDialogs!];
  }

  private setPartialActiveIncomingDialogs(dialogs: Dialog[]) {
    if (dialogs.length === 0) return;

    let hasActiveDialogsUpdated: boolean = false;
    let hasIncomingDialogsUpdated: boolean = false;

    // loop through the  dialogs and set their state
    dialogs.forEach((dialog) => {
      //check if incoming dialog
      let activeDialog = this.activeDialogs?.find(
        (d) => d.eveCallId === dialog.eveCallId
      );
      if (activeDialog) {
        if (dialog.state === 'terminated') {
          // remove from active logs lists
          this.activeDialogs = this.activeDialogs!.filter(
            (d) => d.eveCallId !== activeDialog!.eveCallId
          );
          hasActiveDialogsUpdated = true;
        } else {
          // update the dialog in incomingDialogs
          Object.assign(activeDialog, dialog);
          hasActiveDialogsUpdated = true;
        }
      } else if (dialog.state === 'confirmed') {
        this.activeDialogs!.push(dialog);
        hasActiveDialogsUpdated = true;
      }

      // find incoming dialog
      let incomingDialog = this.incomingDialogs?.find(
        (d) => d.eveCallId === dialog.eveCallId
      );

      if (incomingDialog) {
        if (dialog.state !== 'early') {
          //remove from incomingDialogs
          this.incomingDialogs = this.incomingDialogs!.filter(
            (d) => d.eveCallId !== incomingDialog!.eveCallId
          );
          hasIncomingDialogsUpdated = true;
        } else {
          // update the dialog in incomingDialogs
          Object.assign(incomingDialog, dialog);
          hasIncomingDialogsUpdated = true;
        }
      } else if (dialog.state === 'early') {
        this.incomingDialogs!.push(dialog);
        hasIncomingDialogsUpdated = true;
      }
    });

    if (hasActiveDialogsUpdated) this.activeDialogs = [...this.activeDialogs!];

    if (hasIncomingDialogsUpdated)
      this.incomingDialogs = [...this.incomingDialogs!];
  }

  private handleCallsOrDialogsChange(): void {
    const currentHasCallsOrDialogsOrConferences = this.hasCallsOrDialogsOrConferences;
    
    // Only handle automatic tab switching on mobile view
    if (this.isMobileView) {
      // Check if hasCallsOrDialogsOrConferences changed from false to true
      if (!this.previousHasCallsOrDialogsOrConferences && currentHasCallsOrDialogsOrConferences) {
        this.selectedTab = 'calls';
      }
      // Check if hasCallsOrDialogsOrConferences changed from true to false
      else if (this.previousHasCallsOrDialogsOrConferences && !currentHasCallsOrDialogsOrConferences) {
        this.selectedTab = 'dialer';
      }
    }
    
    this.previousHasCallsOrDialogsOrConferences = currentHasCallsOrDialogsOrConferences;
  }

  private handleScreenSizeChange(): void {
    const wasMobileView = this.isMobileView;
    this.isMobileView = window.innerWidth < 1024;
    
    // Screen changed from mobile to desktop
    if (wasMobileView && !this.isMobileView) {
      if (this.selectedTab === 'dialer' || this.selectedTab === 'calls') {
        this.selectedTab = 'contacts';
      }
    }
  }
}
